import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
    DataGridPro,
    GridToolbarContainer,
    GridToolbarColumnsButton,
    GridToolbarFilterButton,
    GridToolbarDensitySelector,
    GridPagination,
    GridFilterModel,
    GridSortModel,
    GridSortDirection,
    GridFilterItem,
    GridSortItem
} from '@mui/x-data-grid-pro';
import { Box, FormControlLabel, Switch, Button, CircularProgress } from '@mui/material';
import { useAppDispatch } from '../../context/store';
import { RootState } from '../../context/rootReducer';
import {
    downloadSiteShutdownsCSVAsync,
    searchSiteShutdownsAsync
} from '../../slices/siteShutdowns/siteShutdownSlice';
import {
    siteShutdownColumns,
    customDateColumnType,
    getStatusColor,
    quickFiltersSortOrder
} from './SiteShutdownsProps';
import { ShutdownStatus, DateRange, UiDateRange } from '../../lib/types';
import {
    SIZE_PER_PAGE_LIST,
    START_PAGE,
    DEFAULT_TABLE_SORTING_ORDER,
    DATA_GRID_COMMON_COMPONENTS_PROPS_STYLES,
    DATA_GRID_COMMON_SX_STYLES,
    RP_BLUE,
    TABLE_FILTER_TIP,
    SITE_SHUTDOWN_QUICK_FILTERS_TIP,
    FILTER_TIP_ICON_SIZE,
    DEFAULT_TABLE_SORT_MODEL
} from '../../lib/constants';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import '../../css/siteShutdowns.css';
import { buttonBaseProps } from './SiteShutdownsProps';
import { addDateToFileName } from '../../lib/helpers/helpers';
import { Logger } from '../../lib/utils/logger';
import { SORT_ORDER_DIRECTIONS } from '../../lib/globals';

const DEFAULT_REQUEST_FILTER_ATTRIBUTES = null;
const DEFAULT_REQUEST_SORT_ORDER = [{ column: 'startTime', order: SORT_ORDER_DIRECTIONS.DESCENDING }];

function convertLocalStringToDate(dateString: string): Date {
    const [year, month, day] = dateString.split('-').map((s) => parseInt(s, 10));
    return new Date(year, month - 1, day);
}

function parseLocalDateToUtcMidnight(dateString: string, addOneDay: boolean = false): Date {
    const date = convertLocalStringToDate(dateString);
    if (addOneDay) {
        date.setDate(date.getDate() + 1);
    }
    return date;
}

// Data Grid with logic to invoke API through slice.
const SiteShutdownsDataGrid: React.FunctionComponent = () => {
    const dispatch = useAppDispatch();

    const { siteShutdowns = [], totalCount = 0, isLoading = false, statusTotalCounts } = useSelector(
        (state: RootState) => state.siteShutdowns || {}
    );
    const { userCardholderInformation } = useSelector((state: RootState) => state.userProfile);
    const { filterAttributes, sortOrder } = useSelector((state: RootState) => state.siteShutdowns);

    const [pageNumber, setPageNumber] = useState(START_PAGE);
    const [pageSize, setPageSize] = useState(50);
    const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
    const [sortModel, setSortModel] = useState<GridSortModel>([DEFAULT_TABLE_SORT_MODEL as GridSortItem]);
    const [statusFilter, setStatusFilter] = useState<ShutdownStatus[]>([]);
    const [autoReload, setAutoReload] = useState(true);

    const columnTypes = useMemo(() => ({ customDate: customDateColumnType }), []);

    const tableFilterAndSortHandler = useCallback(() => {
        const apiFilterAttributes: any = {};
        filterModel.items.forEach((filter: GridFilterItem) => {
            if (filter.value === undefined || filter.value === '') return;
            const { columnField, operatorValue, value } = filter;
            const isDateField = ['startTime', 'endTime', 'lastUpdateTime'].includes(columnField as string);

            if (isDateField) {
                type RawDateValue = string | UiDateRange;
                const raw = value as RawDateValue;
                let r: UiDateRange;
                const dateRangeFilter: DateRange = {};
                if (!apiFilterAttributes[columnField]) {
                    apiFilterAttributes[columnField] = [];
                }

                switch (operatorValue) {
                    case 'between':
                        r = raw as UiDateRange;
                        dateRangeFilter.from = parseLocalDateToUtcMidnight(r.from);
                        dateRangeFilter.to = parseLocalDateToUtcMidnight(r.to, true);
                        break;
                    case 'after':
                        dateRangeFilter.from = parseLocalDateToUtcMidnight(raw as string);
                        dateRangeFilter.to = null;
                        break;
                    case 'before':
                        dateRangeFilter.to = parseLocalDateToUtcMidnight(raw as string, true);
                        dateRangeFilter.from = null;
                        break;
                }
                apiFilterAttributes[columnField].push(dateRangeFilter);
            } else {
                if (!apiFilterAttributes[columnField]) apiFilterAttributes[columnField] = [];
                apiFilterAttributes[columnField].push(value);
            }
        });

        const apiSortOrder =
            sortModel.length > 0
                ? { column: sortModel[0].field, order: sortModel[0].sort || 'desc' }
                : { column: 'lastUpdateTime', order: 'desc' };

        dispatch(
            searchSiteShutdownsAsync(pageNumber, pageSize, apiFilterAttributes, statusFilter, apiSortOrder)
        );
    }, [dispatch, filterModel, sortModel, pageSize, pageNumber, statusFilter]);

    // Auto-Reload Feature
    useEffect(() => {
        if (!autoReload) return;
        tableFilterAndSortHandler();

        const intervalId = window.setInterval(() => {
            tableFilterAndSortHandler();
        }, 30000);

        return () => window.clearInterval(intervalId);
    }, [autoReload, tableFilterAndSortHandler]);

    // This effect handles fetching data when filters or sorting change.
    useEffect(() => {
        tableFilterAndSortHandler();
    }, [tableFilterAndSortHandler]);

    // For "Clear Filters"
    const handleFilterChange = useCallback((newFilterModel: GridFilterModel) => {
        setPageNumber(START_PAGE);
        setFilterModel(newFilterModel);
        setStatusFilter([]);
    }, []);

    // For Quick Status Filters
    const handleStatusFilterClick = useCallback(
        (status: ShutdownStatus) => {
            setPageNumber(START_PAGE);

            setStatusFilter((prev) => {
                const deselecting = prev.includes(status);
                const next = deselecting ? prev.filter((s) => s !== status) : [...prev, status];

                if (next.length === 1) {
                    setSortModel([quickFiltersSortOrder[next[0]]]);
                } else {
                    setSortModel([DEFAULT_TABLE_SORT_MODEL as GridSortItem]);
                }

                return next;
            });
        },
        [setPageNumber, setStatusFilter, setSortModel, quickFiltersSortOrder]
    );

    // Page Number Handler
    const onPageChangeHandler = (newPage: number) => {
        setPageNumber(newPage + START_PAGE);
    };

    const onPageSizeChangeHandler = (newPageSize: number) => {
        setPageNumber(START_PAGE);
        setPageSize(newPageSize);
    };

    const CustomToolbar = (props: any): JSX.Element => {
        return (
            <Box>
                <StatusToggleBar
                    onStatusClick={handleStatusFilterClick}
                    currentFilter={statusFilter}
                    statusCounts={statusTotalCounts}
                    isCountsLoading={isLoading}
                />
                <GridToolbarContainer>
                    <GridToolbarFilterButton {...props} />
                    <GridToolbarDensitySelector {...props} />
                    <GridToolbarColumnsButton {...props} />
                    <Button
                        {...buttonBaseProps}
                        onClick={async () => {
                            dispatch(
                                downloadSiteShutdownsCSVAsync(
                                    addDateToFileName('SiteShutdowns.csv'),
                                    DEFAULT_REQUEST_FILTER_ATTRIBUTES,
                                    DEFAULT_REQUEST_SORT_ORDER
                                )
                            );
                            Logger.info(
                                `${userCardholderInformation.login} is initiating a Full Export download of Site Shutdowns`,
                                {
                                    employeeId: userCardholderInformation.empId,
                                    userAlias: userCardholderInformation.login
                                }
                            );
                        }}
                    >
                        Full Export
                    </Button>
                    <Button
                        {...buttonBaseProps}
                        onClick={async () => {
                            dispatch(
                                downloadSiteShutdownsCSVAsync(
                                    addDateToFileName('Filtered-SiteShutdowns.csv'),
                                    filterAttributes,
                                    sortOrder
                                )
                            );
                            Logger.info(
                                `${userCardholderInformation.login} is initiating a Filtered Export download of Site Shutdowns`,
                                {
                                    employeeId: userCardholderInformation.empId,
                                    userAlias: userCardholderInformation.login,
                                    filterAttributes: filterAttributes
                                }
                            );
                        }}
                    >
                        Filtered Export
                    </Button>
                    <Button onClick={() => handleFilterChange({ items: [] })} size='small'>
                        Clear Filters
                    </Button>
                    <FormControlLabel
                        control={
                            <Switch
                                sx={{ transform: 'translateY(2px)' }}
                                checked={autoReload}
                                onChange={(e) => setAutoReload(e.target.checked)}
                            />
                        }
                        label='Auto Reload'
                        componentsProps={{
                            typography: {
                                sx: {
                                    color: RP_BLUE,
                                    fontSize: '13px',
                                    fontFamily: 'Inter',
                                    paddingTop: '8px'
                                }
                            }
                        }}
                        labelPlacement='start'
                    />
                </GridToolbarContainer>
            </Box>
        );
    };

    return (
        <div style={{ width: '100%' }}>
            <DataGridPro
                rows={siteShutdowns}
                getRowId={(row) => row.preCogId}
                columns={siteShutdownColumns}
                rowCount={totalCount}
                loading={isLoading}
                pagination
                filterMode='server'
                sortingMode='server'
                paginationMode='server'
                page={pageNumber - START_PAGE}
                pageSize={pageSize}
                filterModel={filterModel}
                sortModel={sortModel}
                onPageChange={onPageChangeHandler}
                onPageSizeChange={onPageSizeChangeHandler}
                onFilterModelChange={handleFilterChange}
                onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
                autoHeight
                columnTypes={columnTypes}
                rowsPerPageOptions={SIZE_PER_PAGE_LIST}
                sortingOrder={DEFAULT_TABLE_SORTING_ORDER as GridSortDirection[]}
                components={{
                    Toolbar: CustomToolbar,
                    Pagination: GridPagination
                }}
                componentsProps={{
                    ...DATA_GRID_COMMON_COMPONENTS_PROPS_STYLES,
                    filterPanel: {
                        sx: {
                            '& .MuiDataGrid-filterFormLinkOperatorInput': { display: 'none' },
                            '& .MuiDataGrid-filterForm': { minWidth: '600px' },
                            '& .MuiTextField-root': { minWidth: '125px' }
                        }
                    }
                }}
                disableColumnMenu={true}
                disableSelectionOnClick
                sx={DATA_GRID_COMMON_SX_STYLES}
            />
        </div>
    );
};

// Quick Filter Status Toggle Bar w/ Info Text
const StatusToggleBar: React.FC<{
    onStatusClick: (status: ShutdownStatus) => void;
    currentFilter: ShutdownStatus[];
    statusCounts: { [key in ShutdownStatus]?: number };
    isCountsLoading: boolean;
}> = ({ onStatusClick, currentFilter, statusCounts, isCountsLoading }) => {
    const [isTextVisible, setIsTextVisible] = useState(false);
    return (
        <div style={{ position: 'relative' }}>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    marginBottom: 10,
                    gap: '8px'
                }}
            >
                {Object.values(ShutdownStatus).map((status) => {
                    const isActive = currentFilter.includes(status);
                    const color = getStatusColor(status);
                    const count = statusCounts[status];

                    return (
                        <div
                            key={status}
                            onClick={() => onStatusClick(status)}
                            style={{
                                cursor: 'pointer',
                                userSelect: 'none',
                                padding: '6px 12px',
                                borderRadius: 4,
                                fontWeight: 'bold',
                                backgroundColor: isActive ? 'transparent' : color,
                                color: isActive ? color : '#fff',
                                border: `2px solid ${color}`,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                        >
                            {status}
                            {isCountsLoading ? (
                                <CircularProgress size={14} color='inherit' />
                            ) : (
                                `(${count ?? 0})`
                            )}
                        </div>
                    );
                })}

                <AiOutlineInfoCircle
                    onClick={() => setIsTextVisible(!isTextVisible)}
                    size={FILTER_TIP_ICON_SIZE}
                    color={RP_BLUE}
                    style={{ cursor: 'pointer' }}
                />
                {isTextVisible && <i className='info-text'>{SITE_SHUTDOWN_QUICK_FILTERS_TIP}</i>}
            </div>
        </div>
    );
};

export default SiteShutdownsDataGrid;
